import { isDevelopmentAssessment } from '../../shared/developmentLevels';

// Numeric course-assessment scores are points on a 0–100 scale.
// A missing result and an "absent" result are not zero; zero is a valid score.
export const assessmentScore = (row) => {
  if (isDevelopmentAssessment(row)) return null;
  if (String(row?.result_status || '').toLowerCase() === 'absent') return null;
  const value = row?.result_value;
  if (value === null || value === undefined || String(value).trim() === '') return null;
  if (!/^(?:\d+)(?:[.,]\d+)?$/.test(String(value).trim())) return null;
  const parsed = Number(String(value).replace(',', '.'));
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= 100 ? parsed : null;
};

export const assessmentSubjectKey = (row) => {
  if (row?.lesson_id !== null && row?.lesson_id !== undefined) return `lesson:${row.lesson_id}`;
  const name = String(row?.lesson_name || '').trim();
  return name ? `name:${name.toLocaleLowerCase('tr-TR')}` : null;
};

export const formatAssessmentAverage = (average) => (
  average === null || !Number.isFinite(average)
    ? '—'
    : Number.isInteger(average) ? String(average) : average.toFixed(1).replace('.', ',')
);

const averageFor = (items) => (
  items.length ? items.reduce((total, item) => total + item, 0) / items.length : null
);

export const summarizeAssessmentScores = (rows) => {
  const scores = [];
  const subjectMap = new Map();
  const studentMap = new Map();
  let unassignedCount = 0;

  (rows || []).forEach((row) => {
    const score = assessmentScore(row);
    if (score === null) return;
    scores.push(score);

    const studentId = String(row.student_id ?? '');
    if (studentId) {
      if (!studentMap.has(studentId)) studentMap.set(studentId, { scores: [], subjects: new Map() });
      studentMap.get(studentId).scores.push(score);
    }

    const key = assessmentSubjectKey(row);
    if (!key) {
      unassignedCount += 1;
      return;
    }
    const label = String(row.lesson_name || '').trim() || 'Ders';
    if (!subjectMap.has(key)) subjectMap.set(key, { key, label, scores: [], studentIds: new Set() });
    const subject = subjectMap.get(key);
    subject.scores.push(score);
    if (studentId) subject.studentIds.add(studentId);

    if (studentId) {
      const student = studentMap.get(studentId);
      if (!student.subjects.has(key)) student.subjects.set(key, []);
      student.subjects.get(key).push(score);
    }
  });

  return {
    average: averageFor(scores),
    scoreCount: scores.length,
    studentCount: studentMap.size,
    unassignedCount,
    subjects: [...subjectMap.values()]
      .map(({ key, label, scores: subjectScores, studentIds }) => ({
        key,
        label,
        average: averageFor(subjectScores),
        count: subjectScores.length,
        studentCount: studentIds.size,
      }))
      .sort((a, b) => a.label.localeCompare(b.label, 'tr-TR')),
    byStudent: Object.fromEntries([...studentMap.entries()].map(([id, entry]) => [
      id, {
        average: averageFor(entry.scores),
        count: entry.scores.length,
        subjects: Object.fromEntries([...entry.subjects.entries()].map(([key, values]) => [
          key, { average: averageFor(values), count: values.length },
        ])),
      },
    ])),
  };
};
